import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Gift, Percent, Zap, Clock, RefreshCw, Sparkles,
  Calendar, ChevronRight, Timer, CheckCircle, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin } from "@/lib/celebrations";

export const PromosPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetchPromos();
    // Refresh every minute
    const interval = setInterval(fetchPromos, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPromos = async () => {
    try {
      const response = await axios.get(`${API}/promos/active`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromos(response.data.promos || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const claimCashback = async () => {
    setClaiming(true);
    try {
      const response = await axios.post(`${API}/promos/claim-cashback`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      celebrateWin(1, response.data.cashback);
      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim cashback");
    } finally {
      setClaiming(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return "";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const promoIcons = {
    "percent": Percent,
    "zap": Zap,
    "clock": Clock,
    "gift": Gift,
    "refresh-cw": RefreshCw
  };

  const promoColors = {
    "cashback_wednesday": { bg: "from-[#00E701]/20", border: "#00E701", text: "#00E701" },
    "weekend_double_xp": { bg: "from-[#FFD600]/20", border: "#FFD600", text: "#FFD600" },
    "happy_hour": { bg: "from-[#FF6B00]/20", border: "#FF6B00", text: "#FF6B00" },
    "first_deposit_bonus": { bg: "from-[#007AFF]/20", border: "#007AFF", text: "#007AFF" },
    "reload_bonus": { bg: "from-[#FF007A]/20", border: "#FF007A", text: "#FF007A" }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#FFD600] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF007A] to-[#FFD600] flex items-center justify-center">
            <Gift className="w-6 h-6 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-['Rajdhani']">Promotions</h1>
            <p className="text-[#A1A1AA] text-sm">Exclusive bonuses and rewards</p>
          </div>
        </div>

        {/* Active Promos Banner */}
        <div className="p-4 rounded-xl bg-gradient-to-r from-[#FFD600]/10 to-[#FF6B00]/10 border border-[#FFD600]/30">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FFD600]" />
            <span className="text-[#FFD600] font-bold">
              {promos.filter(p => p.is_active).length} Active Promotions Right Now!
            </span>
          </div>
        </div>

        {/* Promo Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {promos.map((promo) => {
            const colors = promoColors[promo.id] || promoColors.first_deposit_bonus;
            const IconComponent = promoIcons[promo.icon] || Gift;
            
            return (
              <div 
                key={promo.id}
                className={`p-5 rounded-2xl bg-gradient-to-br ${colors.bg} to-transparent border transition-all ${
                  promo.is_active ? 'border-opacity-50 scale-100' : 'border-white/10 opacity-60'
                }`}
                style={{ borderColor: promo.is_active ? colors.border : undefined }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${colors.border}20` }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: colors.text }} />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">{promo.name}</h3>
                      <p className="text-[#A1A1AA] text-sm">{promo.description}</p>
                    </div>
                  </div>
                  
                  {promo.is_active ? (
                    <Badge className="bg-[#00E701]/20 text-[#00E701] border-0">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </Badge>
                  ) : (
                    <Badge className="bg-white/10 text-white/50 border-0">
                      <Clock className="w-3 h-3 mr-1" />
                      Upcoming
                    </Badge>
                  )}
                </div>
                
                {promo.is_active && promo.time_remaining && (
                  <div className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-3">
                    <Timer className="w-4 h-4" />
                    <span>Ends in {formatTime(promo.time_remaining)}</span>
                  </div>
                )}
                
                {/* Promo-specific actions */}
                {promo.id === "cashback_wednesday" && promo.is_active && (
                  <Button
                    onClick={claimCashback}
                    disabled={claiming}
                    className="w-full mt-2"
                    style={{ backgroundColor: colors.border, color: "black" }}
                  >
                    {claiming ? "Claiming..." : "Claim Cashback"}
                  </Button>
                )}
                
                {promo.id === "first_deposit_bonus" && (
                  <div className="mt-3 p-3 rounded-lg bg-black/20">
                    <p className="text-white text-sm">
                      <span style={{ color: colors.text }}>100% match</span> up to ₹5,000 on your first deposit!
                    </p>
                  </div>
                )}
                
                {promo.id === "weekend_double_xp" && (
                  <div className="mt-3 p-3 rounded-lg bg-black/20">
                    <p className="text-white text-sm">
                      <span style={{ color: colors.text }}>2x VIP progress</span> on all wagers this weekend!
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Promo Schedule */}
        <div className="p-6 rounded-2xl bg-[#1A1B1F] border border-white/10">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#007AFF]" />
            Weekly Promo Schedule
          </h3>
          
          <div className="space-y-3">
            {[
              { day: "Monday", promo: "Reload Bonus", color: "#FF007A" },
              { day: "Tuesday", promo: "-", color: "#52525B" },
              { day: "Wednesday", promo: "Cashback Wednesday", color: "#00E701" },
              { day: "Thursday", promo: "-", color: "#52525B" },
              { day: "Friday", promo: "-", color: "#52525B" },
              { day: "Saturday", promo: "Weekend Double XP", color: "#FFD600" },
              { day: "Sunday", promo: "Weekend Double XP", color: "#FFD600" },
            ].map((item, i) => (
              <div 
                key={i}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  new Date().getDay() === (i + 1) % 7 ? 'bg-white/10 border border-white/20' : 'bg-white/5'
                }`}
              >
                <span className="text-white font-medium">{item.day}</span>
                <span style={{ color: item.color }}>{item.promo}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Happy Hour */}
        <div className="p-6 rounded-2xl bg-gradient-to-br from-[#FF6B00]/20 to-transparent border border-[#FF6B00]/30">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="w-8 h-8 text-[#FF6B00]" />
            <div>
              <h3 className="text-lg font-bold text-white">Happy Hour</h3>
              <p className="text-[#A1A1AA]">Every day 6 PM - 9 PM</p>
            </div>
          </div>
          <p className="text-white">
            Get <span className="text-[#FF6B00] font-bold">50% extra credits</span> on all deposits during happy hour!
          </p>
        </div>
      </div>
    </Layout>
  );
};
